import React, { useState, useEffect } from "react";
import AxiosInstance from "../Home/AxiosInstance"
import { useNavigate } from "react-router-dom";
import "./traineePortal.css";

const TraineeAddress = () => {
  const [traineeId, setTraineeId] = useState("");

  const [addressSearch, setAddressSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [addressType, setAddressType] = useState("");

  const [stateOptions, setStateOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [addressTypeOptions, setAddressTypeOptions] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const savedTraineeId = localStorage.getItem("traineeId");
    if (savedTraineeId) setTraineeId(savedTraineeId);

    AxiosInstance.get("companies/address/types/")
      .then(res => setAddressTypeOptions(res.data))
      .catch(err => console.error("Error loading address types", err));

    AxiosInstance.get("companies/countries/")
      .then(res => setCountryOptions(res.data))
      .catch(err => console.error("Error loading countries", err));
  }, []);

  useEffect(() => {
    if (country) {
      AxiosInstance.get(`companies/states/${country}/`)
        .then(res => setStateOptions(res.data))
        .catch(err => console.error("Error loading states", err));
    } else {
      setStateOptions([]);
    }
  }, [country]);

  useEffect(() => {
    if (!addressSearch) {
      setSuggestions([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const res = await AxiosInstance.get("companies/get-address/", {
          params: { address: addressSearch }
        });
        setSuggestions(res.data.suggestions || []);
      } catch (err) {
        console.error("Autocomplete failed", err);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [addressSearch]);

  const handleAddressSelect = async (placeId, description) => {
    setSuggestions([]);
    setAddressSearch("");

    try {
      const res = await AxiosInstance.get("companies/get-address-details/", {
        params: { placeId }
      });

      const data = res.data;

      setAddressLine1(data.addressLine1 || "");
      setAddressLine2(data.addressLine2 || "");
      setCity(data.city || "");
      setZipCode(data.postalCode || "");

      // Match by name from dropdown options
      const matchedCountry = countryOptions.find(c => c.country_name === data.country);
      const matchedState = stateOptions.find(s => s.state_name === data.state);

      setCountry(matchedCountry ? matchedCountry.country_id : "");
      setState(matchedState ? matchedState.state_id : "");

    } catch (err) {
      console.error("Error fetching address details", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      trainee: traineeId,
      address_line1: addressLine1,
      address_line2: addressLine2,
      city,
      state,
      country,
      zip_code: zipCode,
      address_type: addressType,
    };

    try {
      const res = await AxiosInstance.post("companies/address", payload, {
        headers: { "Content-Type": "application/json" }
      });

      if (res.status === 201) navigate(-1);
      else console.error("Failed to save address:", res);
    } catch (err) {
      console.error("Error saving address", err);
    }
  };

  return (
    <div className="als-trainee-form-page">
      <div className="als-trainee-form-card">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">Add Address</h2>
            <button type="button" className="btn-close" onClick={() => navigate(-1)} aria-label="Close" />
          </div>

          <form onSubmit={handleSubmit}>
            {/* Autocomplete field */}
            <div className="mb-3 position-relative">
              <label className="form-label">Search Address</label>
              <input
                type="text"
                className="form-control"
                placeholder="Type address..."
                value={addressSearch}
                onChange={(e) => setAddressSearch(e.target.value)}
              />
              {suggestions.length > 0 && (
                <ul className="list-group position-absolute w-100" style={{ zIndex: 999 }}>
                  {suggestions.map((item, index) => (
                    <li
                      key={index}
                      className="list-group-item list-group-item-action"
                      onClick={() => handleAddressSelect(item.place_id, item.description)}
                      style={{ cursor: "pointer" }}
                    >
                      {item.description}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">Address Line 1</label>
              <input
                type="text"
                className="form-control"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Address Line 2</label>
              <input
                type="text"
                className="form-control"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-control"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Country</label>
              <select
                className="form-select"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
              >
                <option value="">Select Country</option>
                {countryOptions.map(c => (
                  <option key={c.country_id} value={c.country_id}>
                    {c.country_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">State</label>
              <select
                className="form-select"
                value={state}
                onChange={(e) => setState(e.target.value)}
                disabled={!country}
                required
              >
                <option value="">Select State</option>
                {stateOptions.map(s => (
                  <option key={s.state_id} value={s.state_id}>
                    {s.state_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Zip Code</label>
              <input
                type="text"
                className="form-control"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                required
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Address Type</label>
              <select
                className="form-select"
                value={addressType}
                onChange={(e) => setAddressType(e.target.value)}
                required
              >
                <option value="">Select Address Type</option>
                {addressTypeOptions.map(type => (
                  <option key={type.address_type_id} value={type.address_type_id}>
                    {type.address_type}
                  </option>
                ))}
              </select>
            </div>

            <div className="d-grid">
              <button type="submit" className="btn btn-primary">
                Save Address
              </button>
            </div>
          </form>
      </div>
    </div>
  );
};

export default TraineeAddress;

